import { useEffect, useState } from 'react';
import { DEVICE_BUILDS_CHANGED_EVENT, getDeviceBuilds, isActiveBuild } from '../services/DeviceBuildsService';

/**
 * The project's device builds (GET /device-builds: `{ available, apps, builds }`) for the whole page: one answer per
 * project shared by every component that asks (Build for devices in the service header, the builds card, the Publish
 * dialog), read when a component mounts and the answer is older than 5 s, every 5 s while a build is queued or
 * running, and at once on `flow:device-builds-changed`. `{ data, loading, error, refresh }`; an older sandbox or no
 * vfs-service: `data` null, `error` set (callers show nothing).
 */
export const DEVICE_BUILDS_POLL_MS = 5000;
const EMPTY = Object.freeze({ data: null, loading: false, error: null });
const entries = new Map();

function entryOf(projectId) {
  if (!entries.has(projectId)) {
    entries.set(projectId, { value: { ...EMPTY, loading: true }, at: 0, inflight: null, listeners: new Set(), timer: null });
  }
  return entries.get(projectId);
}

function schedule(projectId, entry) {
  clearTimeout(entry.timer);
  entry.timer = null;
  const active = (entry.value.data?.builds || []).some(isActiveBuild);
  if (active && entry.listeners.size > 0) {
    // eslint-disable-next-line no-use-before-define
    entry.timer = setTimeout(() => { refreshDeviceBuilds(projectId); }, DEVICE_BUILDS_POLL_MS);
  }
}

/** Reads the builds again (one call at a time per project); resolves to the new value. */
export function refreshDeviceBuilds(projectId) {
  if (!projectId) { return Promise.resolve(EMPTY); }
  const entry = entryOf(projectId);
  if (entry.inflight) { return entry.inflight; }
  entry.inflight = getDeviceBuilds(projectId)
    .then((data) => ({ data: data || null, loading: false, error: null }))
    .catch((error) => ({ data: entry.value.data, loading: false, error }))
    .then((value) => {
      entry.at = Date.now();
      entry.inflight = null;
      entry.value = value;
      entry.listeners.forEach((listener) => listener(value));
      schedule(projectId, entry);
      return value;
    });
  return entry.inflight;
}

/** For tests: forget every project's answer. */
export function resetDeviceBuildsCache() {
  entries.forEach((entry) => clearTimeout(entry.timer));
  entries.clear();
}

export default function useDeviceBuilds(projectId) {
  const [value, setValue] = useState(() => (projectId ? entryOf(projectId).value : EMPTY));

  useEffect(() => {
    if (!projectId) { setValue(EMPTY); return undefined; }
    const entry = entryOf(projectId);
    setValue(entry.value);
    entry.listeners.add(setValue);
    if (Date.now() - entry.at >= DEVICE_BUILDS_POLL_MS) { refreshDeviceBuilds(projectId); } else { schedule(projectId, entry); }
    const onChanged = (event) => { if (!event.detail?.projectId || event.detail.projectId === projectId) { refreshDeviceBuilds(projectId); } };
    window.addEventListener(DEVICE_BUILDS_CHANGED_EVENT, onChanged);
    return () => {
      entry.listeners.delete(setValue);
      window.removeEventListener(DEVICE_BUILDS_CHANGED_EVENT, onChanged);
      if (entry.listeners.size === 0) { clearTimeout(entry.timer); entry.timer = null; }
    };
  }, [projectId]);

  return { ...value, refresh: () => refreshDeviceBuilds(projectId) };
}
